import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Save, Plus, Trash2, GripVertical } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const HeaderEditor = () => {
  const [headerConfig, setHeaderConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadHeader();
  }, []);

  const loadHeader = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cms/header`);
      const data = await response.json();
      setHeaderConfig(data);
    } catch {
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/header`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(headerConfig)
      });

      if (response.ok) {
        alert('✅ Header mis à jour avec succès !');
      } else {
        alert('❌ Erreur lors de la mise à jour');
      }
    } catch {
      alert('❌ Erreur lors de la mise à jour');
    }
    setSaving(false);
  };

  const addMenuItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      label: 'Nouveau menu',
      type: 'link',
      url: '/nouvelle-page'
    };
    setHeaderConfig({
      ...headerConfig,
      menu_items: [...(headerConfig.menu_items || []), newItem]
    });
  };

  const updateMenuItem = (index, field, value) => {
    const newMenuItems = [...headerConfig.menu_items];
    newMenuItems[index] = { ...newMenuItems[index], [field]: value };
    setHeaderConfig({ ...headerConfig, menu_items: newMenuItems });
  };

  const deleteMenuItem = (index) => {
    const newMenuItems = headerConfig.menu_items.filter((_, i) => i !== index);
    setHeaderConfig({ ...headerConfig, menu_items: newMenuItems });
  };

  const addSubItem = (menuIndex) => {
    const newMenuItems = [...headerConfig.menu_items];
    if (!newMenuItems[menuIndex].items) {
      newMenuItems[menuIndex].items = [];
    }
    newMenuItems[menuIndex].items.push({
      label: 'Nouveau sous-menu',
      url: '/nouveau-lien'
    });
    newMenuItems[menuIndex].type = 'dropdown';
    setHeaderConfig({ ...headerConfig, menu_items: newMenuItems });
  };

  if (loading) {
    return <Card><CardContent className="py-12 text-center">Chargement...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Éditeur de Header</CardTitle>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Logo & Colors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">URL du Logo</label>
            <Input
              value={headerConfig?.logo_url || ''}
              onChange={(e) => setHeaderConfig({ ...headerConfig, logo_url: e.target.value })}
              placeholder="/path/to/logo.png"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Texte alternatif</label>
            <Input
              value={headerConfig?.logo_alt || ''}
              onChange={(e) => setHeaderConfig({ ...headerConfig, logo_alt: e.target.value })}
              placeholder="COPPAM"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Couleur de fond</label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={headerConfig?.background_color || '#ffffff'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, background_color: e.target.value })}
                className="w-16 h-10"
              />
              <Input
                value={headerConfig?.background_color || '#ffffff'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, background_color: e.target.value })}
                placeholder="#ffffff"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Couleur du texte</label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={headerConfig?.text_color || '#1f2937'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, text_color: e.target.value })}
                className="w-16 h-10"
              />
              <Input
                value={headerConfig?.text_color || '#1f2937'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, text_color: e.target.value })}
                placeholder="#1f2937"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Couleur bordure</label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={headerConfig?.border_color || '#e5e7eb'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, border_color: e.target.value })}
                className="w-16 h-10"
              />
              <Input
                value={headerConfig?.border_color || '#e5e7eb'}
                onChange={(e) => setHeaderConfig({ ...headerConfig, border_color: e.target.value })}
                placeholder="#e5e7eb"
              />
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Menu de Navigation</h3>
            <Button onClick={addMenuItem} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un menu
            </Button>
          </div>

          <div className="space-y-4">
            {headerConfig?.menu_items?.map((item, index) => (
              <Card key={item.id || index} className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <GripVertical className="h-5 w-5 text-gray-400 mt-2" />
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        placeholder="Libellé"
                        value={item.label}
                        onChange={(e) => updateMenuItem(index, 'label', e.target.value)}
                      />
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={item.type}
                        onChange={(e) => updateMenuItem(index, 'type', e.target.value)}
                      >
                        <option value="link">Lien simple</option>
                        <option value="dropdown">Menu déroulant</option>
                      </select>
                      {item.type === 'link' && (
                        <Input
                          placeholder="URL"
                          value={item.url || ''}
                          onChange={(e) => updateMenuItem(index, 'url', e.target.value)}
                        />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMenuItem(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>

                  {/* Sub-items for dropdown */}
                  {item.type === 'dropdown' && (
                    <div className="ml-8 space-y-2">
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">Sous-menus</p>
                        <Button size="sm" variant="outline" onClick={() => addSubItem(index)}>
                          <Plus className="h-3 w-3 mr-1" /> Ajouter
                        </Button>
                      </div>
                      {item.items?.map((subItem, subIndex) => (
                        <div key={subIndex} className="flex gap-2">
                          <Input
                            placeholder="Libellé"
                            value={subItem.label}
                            onChange={(e) => {
                              const newMenuItems = [...headerConfig.menu_items];
                              newMenuItems[index].items[subIndex].label = e.target.value;
                              setHeaderConfig({ ...headerConfig, menu_items: newMenuItems });
                            }}
                          />
                          <Input
                            placeholder="URL"
                            value={subItem.url}
                            onChange={(e) => {
                              const newMenuItems = [...headerConfig.menu_items];
                              newMenuItems[index].items[subIndex].url = e.target.value;
                              setHeaderConfig({ ...headerConfig, menu_items: newMenuItems });
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Aperçu</h3>
          <div
            className="border rounded-lg p-4"
            style={{
              backgroundColor: headerConfig?.background_color,
              borderColor: headerConfig?.border_color,
              color: headerConfig?.text_color
            }}
          >
            <div className="flex justify-between items-center">
              <div className="font-bold">{headerConfig?.logo_alt || 'LOGO'}</div>
              <div className="flex gap-4">
                {headerConfig?.menu_items?.map((item, i) => (
                  <div key={i} className="cursor-pointer hover:opacity-70">
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HeaderEditor;